import type { AccessBlock } from '@shared/entitlementAccess';
import type { SandboxViewState } from '@shared/sandbox';
import type { TeamPolicy } from '@shared/teamPolicy';
import { getDeviceTimeZone } from '@/utils/deviceTimeZone';

import { fetchWithConfig } from './apiUtils';

interface QueryConfig {
  intKeys?: string[];
  stringKeys?: string[];
  /** Boolean flags: serialized as 'true' only when explicitly true; omitted otherwise. */
  boolKeys?: string[];
}

interface AccessibleUsersParams {
  page?: number;
  limit?: number;
  role?: string;
  search?: string;
  userStatus?: string;
  hasOverrides?: boolean;
  /** Drop nonprofit-cohort teachers (the super-admin My Classes picker). */
  excludeNonprofit?: boolean;
  teamId?: string;
  teamSlug?: string;
  partnershipKind?: string;
  productMode?: string;
  cohortLabel?: string;
  orgLevel?: string;
  examLevel?: string;
}

interface StudentTeacherLinksParams {
  page?: number;
  limit?: number;
  search?: string;
  teacherId?: string;
  status?: string;
  teamId?: string;
  teamSlug?: string;
  partnershipKind?: string;
  productMode?: string;
  cohortLabel?: string;
  orgLevel?: string;
  examLevel?: string;
}

interface StudentClassLinksParams {
  page?: number;
  limit?: number;
  search?: string;
  classCodeId?: string;
  status?: string;
  teamId?: string;
  teamSlug?: string;
  partnershipKind?: string;
  productMode?: string;
  cohortLabel?: string;
  orgLevel?: string;
  examLevel?: string;
}

export interface LinkedClass {
  classCodeId: string;
  classCode: string;
  label?: string | null;
}

export interface StudentClassRow {
  id: string;
  studentUserId: string;
  studentAuth0Id?: string;
  studentEmail?: string | null;
  studentName?: string | null;
  classes: LinkedClass[];
  classCount: number;
}

export interface StudentClassLinksResponse {
  items: StudentClassRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface LinkedStudentsParams {
  page?: number;
  limit?: number;
  search?: string;
}

interface OnboardPayload {
  accessCode: string;
  consentTerms: boolean;
  consentPrivacy: boolean;
}

/**
 * Teacher-side sample-student view, computed server-side on the role payload.
 *
 * `available` means this account is allowed to switch (flagged teacher with a
 * linked sample student); `active` means the current request is already being
 * answered as the sample student, in which case the rest of this payload —
 * role, status, team policy, displayName — describes the sample student, not
 * the signed-in teacher. `actor` keeps the real teacher's name available while
 * active so the UI can name who is really signed in.
 */
export interface DemoViewState {
  available: boolean;
  active: boolean;
  persona: { displayName: string } | null;
  actor: { displayName: string | null };
}

/** Result of entering or leaving the sample-student view. */
export interface DemoViewSwitchResponse {
  active: boolean;
}

interface UserRole {
  role: string;
  userStatus?: string;
  displayName?: string;
  teamPolicy?: TeamPolicy;
  /**
   * Server-computed onboarding-gate flag: true when the student must sit the
   * cold-start placement diagnostic before any other practice. The client
   * route guard keys off this — never off client-side heuristics.
   */
  owesDiagnostic?: boolean;
  /**
   * Server-computed access gate from the entitlement access policy: non-null
   * when the student is blocked (e.g. expired trial) and must be routed to a
   * gate screen. The client route guard keys off this. Null/absent = no block.
   */
  accessBlock?: AccessBlock | null;
  /**
   * Sample-student view state for this account. Absent on servers that predate
   * the feature — callers treat missing/malformed as "no switch available".
   */
  demoView?: DemoViewState;
  sandboxView?: SandboxViewState;
  effectiveUserId?: string;
  effectiveAuth0Id?: string;
  [key: string]: unknown;
}

export interface UserProfile {
  _id: string;
  displayName?: string;
  email?: string;
  role?: string;
  orgLevel?: string;
  featureOverrideCount?: number;
  [key: string]: unknown;
}

interface AccessCheckResponse {
  hasAccess: boolean;
  [key: string]: unknown;
}

interface AccessibleUsersResponse {
  items: UserProfile[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

interface StudentTeacherLink {
  _id: string;
  studentUserId: string;
  teacherUserId: string;
  status: string;
  [key: string]: unknown;
}

interface StudentTeacherLinksResponse {
  links: StudentTeacherLink[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

export interface LinkedStudentsResponse {
  students: UserProfile[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

const buildQueryString = (
  params: Record<string, unknown> | undefined,
  config: QueryConfig
): string => {
  if (!params || typeof params !== 'object') return '';

  const query = new URLSearchParams();

  for (const key of config.intKeys || []) {
    if (params[key] != null) query.set(key, String(params[key]));
  }

  for (const key of config.stringKeys || []) {
    if (typeof params[key] !== 'string') continue;
    const trimmed = (params[key] as string).trim();
    if (trimmed) {
      query.set(key, trimmed);
    }
  }

  for (const key of config.boolKeys || []) {
    if (params[key] === true) query.set(key, 'true');
  }

  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

const buildAccessibleUsersQuery = (params?: AccessibleUsersParams): string =>
  buildQueryString(params as Record<string, unknown>, {
    intKeys: ['page', 'limit'],
    stringKeys: [
      'role',
      'search',
      'userStatus',
      'teamId',
      'teamSlug',
      'partnershipKind',
      'productMode',
      'cohortLabel',
      'orgLevel',
      'examLevel',
    ],
    boolKeys: ['hasOverrides', 'excludeNonprofit'],
  });

const buildStudentTeacherLinksQuery = (
  params?: StudentTeacherLinksParams
): string =>
  buildQueryString(params as Record<string, unknown>, {
    intKeys: ['page', 'limit'],
    stringKeys: [
      'search',
      'teacherId',
      'status',
      'teamId',
      'teamSlug',
      'partnershipKind',
      'productMode',
      'cohortLabel',
      'orgLevel',
      'examLevel',
    ],
  });

const buildStudentClassLinksQuery = (
  params?: StudentClassLinksParams
): string =>
  buildQueryString(params as Record<string, unknown>, {
    intKeys: ['page', 'limit'],
    stringKeys: [
      'search',
      'classCodeId',
      'status',
      'teamId',
      'teamSlug',
      'partnershipKind',
      'productMode',
      'cohortLabel',
      'orgLevel',
      'examLevel',
    ],
  });

const buildLinkedStudentsQuery = (params?: LinkedStudentsParams): string =>
  buildQueryString(params as Record<string, unknown>, {
    intKeys: ['page', 'limit'],
    stringKeys: ['search'],
  });

export const userApi = {
  async getUserRole(
    userId: string,
    email: string,
    token: string
  ): Promise<UserRole> {
    const query = new URLSearchParams({ email });
    const timeZone = getDeviceTimeZone();
    if (timeZone) query.set('timeZone', timeZone);
    return fetchWithConfig<UserRole>(
      `/users/${encodeURIComponent(userId === 'preview-user-id' ? 'me' : userId)}/role?${query.toString()}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },

  /**
   * Start answering as the linked sample student. The server sets an HttpOnly
   * cookie; `fetchWithConfig` already sends credentials, so the caller only has
   * to reload afterwards for every user-keyed cache to be rebuilt.
   */
  async enterDemoStudentView(token: string): Promise<DemoViewSwitchResponse> {
    return fetchWithConfig<DemoViewSwitchResponse>('/users/me/demo-view', {
      method: 'POST',
      authToken: token,
      body: JSON.stringify({ view: 'student' }),
    });
  },

  /**
   * Return to the signed-in teacher's own account. Always permitted, even once
   * the switch itself has been revoked, so nobody can be stranded.
   */
  async exitDemoStudentView(token: string): Promise<DemoViewSwitchResponse> {
    return fetchWithConfig<DemoViewSwitchResponse>('/users/me/demo-view', {
      method: 'DELETE',
      authToken: token,
    });
  },

  async onboard(
    { accessCode, consentTerms, consentPrivacy }: OnboardPayload,
    token: string
  ): Promise<Record<string, unknown>> {
    return fetchWithConfig('/users/onboard', {
      method: 'POST',
      authToken: token,
      body: JSON.stringify({
        accessCode: accessCode?.trim?.() ?? accessCode,
        consentTerms: consentTerms === true,
        consentPrivacy: consentPrivacy === true,
      }),
    });
  },

  async getUserProfile(
    userId: string,
    token: string,
    options: { includeLinkedEnrollment?: boolean } = {}
  ): Promise<UserProfile> {
    const query = options.includeLinkedEnrollment
      ? '?includeLinkedEnrollment=true'
      : '';
    return fetchWithConfig<UserProfile>(
      `/users/profile/${encodeURIComponent(userId)}${query}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },

  async updateUserProfile(
    userId: string,
    profileData: Record<string, unknown>,
    token: string
  ): Promise<UserProfile> {
    return fetchWithConfig<UserProfile>(
      `/users/profile/${encodeURIComponent(userId)}`,
      {
        method: 'PUT',
        authToken: token,
        body: JSON.stringify(profileData),
      }
    );
  },

  async updateUserEntitlement(
    userId: string,
    payload: Record<string, unknown> | null,
    token: string
  ): Promise<Record<string, unknown>> {
    return fetchWithConfig(
      `/users/profile/${encodeURIComponent(userId)}/entitlement`,
      {
        method: 'PUT',
        authToken: token,
        body: JSON.stringify(payload || {}),
      }
    );
  },

  /**
   * `acknowledgeBalances` answers the 409 this returns when the student still
   * has money open in a class closing the account takes them out of — send it
   * only after the operator has seen the balances the refusal listed.
   */
  async deactivateUser(
    userId: string,
    token: string,
    acknowledgeBalances = false
  ): Promise<Record<string, unknown>> {
    return fetchWithConfig(
      `/users/profile/${encodeURIComponent(userId)}/deactivate`,
      {
        method: 'POST',
        authToken: token,
        body: JSON.stringify(
          acknowledgeBalances ? { acknowledgeBalances: true } : {}
        ),
      }
    );
  },

  async checkUserAccess(
    targetUserId: string,
    token: string
  ): Promise<AccessCheckResponse> {
    return fetchWithConfig<AccessCheckResponse>(
      `/users/access/${encodeURIComponent(targetUserId)}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },

  async getAccessibleUsers(
    token: string,
    params?: AccessibleUsersParams
  ): Promise<UserProfile[] | AccessibleUsersResponse> {
    const query = buildAccessibleUsersQuery(params);
    const response = await fetchWithConfig<
      UserProfile[] | AccessibleUsersResponse
    >(`/users/accessible${query}`, {
      method: 'GET',
      authToken: token,
    });

    if (!query) {
      if (Array.isArray(response)) return response;
      if (
        !Array.isArray(response) &&
        Array.isArray((response as AccessibleUsersResponse)?.items)
      )
        return (response as AccessibleUsersResponse).items;
      return [];
    }

    return response;
  },

  async getStudentTeacherLinks(
    params: StudentTeacherLinksParams | undefined,
    token: string
  ): Promise<StudentTeacherLinksResponse> {
    const query = buildStudentTeacherLinksQuery(params);
    return fetchWithConfig<StudentTeacherLinksResponse>(
      `/users/student-teacher-links${query}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },

  async updateStudentTeacherLinks(
    studentUserId: string,
    payload: Record<string, unknown> | null,
    token: string
  ): Promise<Record<string, unknown>> {
    return fetchWithConfig(
      `/users/student-teacher-links/${encodeURIComponent(studentUserId)}`,
      {
        method: 'PUT',
        authToken: token,
        body: JSON.stringify(payload || {}),
      }
    );
  },

  async getStudentClassLinks(
    params: StudentClassLinksParams | undefined,
    token: string
  ): Promise<StudentClassLinksResponse> {
    const query = buildStudentClassLinksQuery(params);
    return fetchWithConfig<StudentClassLinksResponse>(
      `/users/student-class-links${query}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },

  /**
   * Replace a student's class set. Removing them from a class they still have a
   * balance in returns 409 `OUTSTANDING_CLASS_BALANCE` with a `balances` array.
   * Re-send with `acknowledgeBalances: true` to leave the balance where it is,
   * and/or `carryBalances` to move it into a class they are being kept in — the
   * slot count is derived server-side, so only the destination is sent.
   *
   * `billingHomes` maps a class to the sibling class its lessons bill through,
   * for a family paying one number for a child in two classes. Sending it states
   * the arrangement WHOLE — a kept class it does not name bills itself — so omit
   * it entirely to leave the existing pools alone. Removing a class another one
   * is billed under, without re-pointing it here, returns 409
   * `POOL_HOME_REMOVED`.
   */
  async updateStudentClassLinks(
    studentUserId: string,
    payload: {
      classCodeIds: string[];
      acknowledgeBalances?: boolean;
      carryBalances?: { fromClassCodeId: string; toClassCodeId: string }[];
      billingHomes?: Record<string, string | null>;
    },
    token: string
  ): Promise<Record<string, unknown>> {
    return fetchWithConfig(
      `/users/student-class-links/${encodeURIComponent(studentUserId)}`,
      {
        method: 'PUT',
        authToken: token,
        body: JSON.stringify(payload || { classCodeIds: [] }),
      }
    );
  },

  async getMyLinkedStudents(
    params: LinkedStudentsParams | undefined,
    token: string
  ): Promise<LinkedStudentsResponse> {
    const query = buildLinkedStudentsQuery(params);
    return fetchWithConfig<LinkedStudentsResponse>(
      `/users/me/linked-students${query}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },
};
